import {messagesService} from "@dv5/api"
import {HideShowButton, Icon, IconName, Input, TagsChooser} from '@dv5/components'
import {DocumentPart, PartType, TagFiltered} from '@dv5/domain'
import React, {useEffect, useRef, useState} from 'react'
import './DocumentParamsEditor.less'
import {partTypesTitle} from '../partTypes'

interface Props {
    documentId: string
    part: DocumentPart
    tags: Array<string>
    allTags: Array<TagFiltered>
    onHeaderInput?: (header: string) => void
    onTagsChange?: (tags: string) => void
    onHeaderChange?: (header: string | null) => void
    onTypeChange?: (type: PartType) => void
    onCancel: () => void
    allowedTypes?: PartType[]
}

interface AssignPartToTags {
    documentId: string
    partId: string
    tags: string
}

const getTagIds = (part: DocumentPart): string[] => (part.tags || '').split(',').filter(Boolean)

const TypeIcon = ({type}: { type: PartType }) => {
    switch (type) {
        case PartType.MARKDOWN:
            return <Icon name={IconName.Markdown}/>
        case PartType.PLAINTEXT:
            return <Icon name={IconName.Plaintext}/>
        case PartType.CSV_TABLE:
            return <Icon name={IconName.CSVTable}/>
        case PartType.DIARY:
            return <Icon name={IconName.Diary}/>
        case PartType.SECRET:
            return <Icon name={IconName.Password}/>
        default:
            return <Icon name={IconName.Plaintext}/>
    }
}

export const DocumentParamsEditor: React.FC<Props> = ({
                                                          documentId,
                                                          part,
                                                          tags,
                                                          allTags,
                                                          onHeaderChange,
                                                          onHeaderInput,
                                                          onTagsChange,
                                                          onTypeChange,
                                                          onCancel,
                                                          allowedTypes,
                                                      }) => {
    const [header, setHeader] = useState<string>(part.partHeader || '')
    const [currentType, setCurrentType] = useState<PartType>(part.partType)
    useEffect(() => {
        setCurrentType(part.partType)
    }, [part.partType])
    const [showTags, setShowTags] = useState(false)
    const [partTags, setPartTags] = useState<Array<TagFiltered>>([])
    const tagDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const canChangeType = !part.partContent || part.partContent.trim() === ''

    const handleTypeChange = (newType: PartType) => {
        setCurrentType(newType)
        if (onTypeChange) {
            onTypeChange(newType)
        }
    }

    const saveHeader = () => {
        if (onHeaderChange) {
            onHeaderChange(header)
        }
    }

    const toggleTags = () => {
        setShowTags(!showTags)
    }

    useEffect(() => {
        const partTagIds = new Set(getTagIds(part))
        const calculatedTags: Array<TagFiltered> = allTags.map(tag => ({
            ...tag,
            checked: partTagIds.has(tag.tag.id),
        }))
        setPartTags(calculatedTags)
    }, [tags, allTags])

    useEffect(() => {
        return () => {
            if (tagDebounceRef.current) {
                clearTimeout(tagDebounceRef.current)
            }
        }
    }, [])

    const changeTags = (newTags: Array<TagFiltered>) => {
        setPartTags(newTags)
        if (onTagsChange) {
            onTagsChange(newTags.filter(tag => tag.checked).map(tag => tag.tag.id).join(','))
            return
        }
        if (tagDebounceRef.current) {
            clearTimeout(tagDebounceRef.current)
        }
        tagDebounceRef.current = setTimeout(() => {
            messagesService.send<AssignPartToTags>('documents.assign-part-to-tags', {
                documentId: documentId,
                partId: part.id,
                tags: newTags.filter(tag => tag.checked).map(tag => tag.tag.id).join(',')
            })
        }, 300)
    }

    return (
        <>
            <div className="document-params-editor">
                <div className="document-params-editor__header-field">
                    <Input id={"documentHeaderFor-" + part.id}
                           label={"Header"}
                           value={header}
                           placeholder={"Please input optional header"}
                           autoFocus={true}
                           withoutHeader={true}
                           saveOnBlur={true}
                           setValue={value => {
                               setHeader(value)
                               onHeaderInput?.(value)
                           }}
                           onConfirm={saveHeader}
                           onCancel={onCancel}
                    />
                </div>

                {(!allowedTypes || allowedTypes.length > 1) && (
                    <div className="document-params-editor__type-selector">
                        <div className="document-params-editor__type-buttons">
                            {(allowedTypes || Object.values(PartType)).map((partType) => {
                                const isForbidden = !allowedTypes && !canChangeType
                                return (
                                    <button
                                        key={partType}
                                        type="button"
                                        onClick={() => handleTypeChange(partType)}
                                        disabled={isForbidden}
                                        className={`document-params-editor__type-button ${
                                            currentType === partType ? 'document-params-editor__type-button--active' : ''
                                        } ${isForbidden ? 'document-params-editor__type-button--disabled' : ''}`}
                                        title={isForbidden ? "Cannot change type when part content is not empty" : partTypesTitle[partType]}
                                    >
                                        <TypeIcon type={partType}/>
                                    </button>
                                )
                            })}
                        </div>
                    </div>
                )}
                <HideShowButton tooltipTextVisible="Hide tags"
                                tooltipTextHidden="Show tags"
                                visible={showTags}
                                onChange={toggleTags}/>
            </div>
            {showTags && <TagsChooser hasLabel={false} tags={partTags} setTags={changeTags}/>}
        </>
    )
}
